import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import assert from 'assert';
import React from 'react';

import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  getPowAgnosticProcedureFromFlowchartVersion,
  getPowAgnosticTemplateFlowchartVersionFromProcedureRun,
} from 'procedures/utils/powAgnosticFlowchartHelpers';
import {ProcedureRunDetailsViewQuery_procedureRun_ProcedureRunNode as ProcedureRunNode} from 'studies/typed/shared/__generated/ProcedureRunDetailsViewQuery';
import {
  FONT_PROXIMA_13,
  FONT_WEIGHT_PROXIMA_REGULAR,
  FONT_WEIGHT_PROXIMA_SEMIBOLD,
} from 'styles/FontConstants';
import FormGroup from 'ui-library/bs/FormGroup';
import {ChipSize} from 'ui-library/chips/Chip';
import ProcedureChip from 'ui-library/chips/ProcedureChip';
import EditableTextField, {TextFieldType} from 'ui-library/inputs/EditableTextField';
import {SettingsLabel} from 'ui-library/SettingsField';
import * as DateUtil from 'util/DateUtil';

import ProcedureRunDescriptionEditor from './ProcedureRunDescriptionEditor';

interface ProcedureRunMetadataTabProps {
  procedureRun: ProcedureRunNode;
  refetch: () => void;
}

export const ProcedureRunMetadataTab = ({
  procedureRun,
  refetch,
}: ProcedureRunMetadataTabProps): JSX.Element => {
  const flowchartVersion = getPowAgnosticTemplateFlowchartVersionFromProcedureRun(procedureRun);
  assert(!isInaccessible(flowchartVersion), 'Expected flowchart version to be accessible.');
  const procedureParent = getPowAgnosticProcedureFromFlowchartVersion(flowchartVersion);

  return (
    <div className={css(styles.container)}>
      <div className={css(styles.nameStyle)}>
        <div className={css(styles.nameTitle)}>
          <SettingsLabel isRequired={false} label="Name" name="name" withBottomMargin={false} />
        </div>
        <EditableTextField
          isDisabled={true}
          requestChange={() => {}}
          value={procedureRun.name}
          type={TextFieldType.TEXT}
        />
      </div>
      <div className={css(styles.descriptionStyle)}>
        <ProcedureRunDescriptionEditor
          procedureRunId={procedureRun.id}
          isBeingCreated={false}
          description={procedureRun.description}
          refetch={refetch}
        />
      </div>
      <div className={css(styles.row)}>
        <FormGroup>
          <div className={css(styles.label)}>Recipe</div>
          <div className={css(styles.value)}>
            <ProcedureChip
              id={procedureParent.id}
              flowchartVersionId={flowchartVersion.id}
              size={ChipSize.MINI}
            />
          </div>
        </FormGroup>
        <div className={css(styles.spacer)} />
        <FormGroup>
          <div className={css(styles.label)}>Created</div>
          <div className={css(styles.value)}>
            {DateUtil.readableTime(procedureRun.createdAt, {showTime: true})}
          </div>
        </FormGroup>
      </div>
    </div>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: Color.WHITE,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  nameStyle: {
    display: 'grid',
    width: '100%',
    maxWidth: '600px',
    paddingTop: 16,
    paddingBottom: 16,
    alignItems: 'flex-start',
  },
  descriptionStyle: {display: 'block', width: '100%', maxWidth: '600px', marginBottom: -10},
  row: {display: 'flex', alignItems: 'center', width: '100%', maxWidth: '600px'},
  label: {...FONT_PROXIMA_13, fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD, color: Color.BLACK, paddingTop: 6},
  value: {
    ...FONT_PROXIMA_13,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    color: Color.BLACK,
    paddingTop: 6,
    display: 'flex',
    alignItems: 'center',
  },
  spacer: {width: 125},
  nameTitle: {marginLeft: -10},
});
